import { useSidebarNavigate } from "@/Components/Sidebar/useSidebarNavigate";
import { api } from "@/lib/api-client";
import type {
  AnnouncementButton,
  GetClientActiveAnnouncement,
} from "@/server-types";
import {
  trackAnnouncementCtaClicked,
  trackAnnouncementDismissed,
  trackAnnouncementShown,
} from "@/utils/announcement-tracking";
import { useDisclosure } from "@heroui/react";
import { useEffect, useRef } from "react";
import { AnnouncementDialog } from "./AnnouncementDialog";
import { runAnnouncementButton } from "./announcementActions";

type ActiveAnnouncement = NonNullable<
  GetClientActiveAnnouncement["response"]["announcement"]
>;

export function AnnouncementModal({
  announcement,
}: {
  announcement: ActiveAnnouncement;
}) {
  const { isOpen, onClose } = useDisclosure({ defaultOpen: true });
  const { mutate: markShown } = api.announcements.usePostAnnouncementShown();
  const { mutate: markClosed } = api.announcements.usePostAnnouncementClosed();
  const navigateFromSidebar = useSidebarNavigate();
  const markedShownFor = useRef<number | null>(null);

  const tracking = {
    announcementId: announcement.id,
    announcementName: announcement.name,
    language: announcement.language,
  };

  useEffect(() => {
    if (markedShownFor.current === announcement.id) return;
    markedShownFor.current = announcement.id;

    markShown(announcement.id);
    trackAnnouncementShown({
      announcementId: announcement.id,
      announcementName: announcement.name,
      language: announcement.language,
    });
  }, [announcement.id, announcement.name, announcement.language, markShown]);

  function close(buttonId?: string) {
    onClose();
    markClosed({ announcementId: announcement.id, buttonId });
  }

  function dismiss() {
    trackAnnouncementDismissed(tracking);
    close();
  }

  function pressButton(button: AnnouncementButton) {
    const clickedButtonId = runAnnouncementButton(button, {
      navigate: navigateFromSidebar,
      openUrl: (url) => window.open(url, "_blank", "noopener,noreferrer"),
    });

    if (!clickedButtonId) {
      dismiss();
      return;
    }

    trackAnnouncementCtaClicked({ ...tracking, buttonId: clickedButtonId });
    close(clickedButtonId);
  }

  function openDashboardLink(path: string) {
    dismiss();
    navigateFromSidebar(path);
  }

  return (
    <AnnouncementDialog
      isOpen={isOpen}
      onClose={dismiss}
      size={announcement.size}
      content={announcement.content}
      onButtonPress={pressButton}
      onDashboardLink={openDashboardLink}
      dataIdentifier="announcement-modal"
    />
  );
}
